'use strict';

/**
 * Overlay for choosing the set of one or multiple items
 *
 * @category   View
 * @module     collection.Set
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var ColidoLayoutView = require('base/Colido.LayoutView');
var ShareColumnsListView = require('../partials/share-columns-list');
var ShareWithConversationsModalView = require('modules/community/views/layout/share-with-conversation-modal');

var SharesCollection = require('modules/share/collections/shares');
var ShareModel = require('modules/share/models/share');

var Clipboard = require('vendor/clipboard-1.3.1');

var template = require('../../templates/layout/share-set-overlay.hbs');

var ShareSetOverlayLayout = ColidoLayoutView.extend({
    template: template,

    regions: {
        shareColumnsRegion: '.share-column-settings-region',
    },

    ui: {
        container: '.modal-share-set',
        buttonClose: '[data-action="close"]',
        buttonDone: '[data-action="done"]',
        inputShareLink: 'input[name="shareLink"]',
        buttonCopyLink: '[data-action="copy-link"]',
        buttonOpenLink: '[data-action="open-link"]',
        buttonSharingOptions: '[data-action="share-options"]',
        linkAccessToggle: 'input[name="externalLinkAccess"]',
        buttonShareLabel: '[data-action="share-label"]',
        buttonShareEmail: '[data-action="share-email"]',
        buttonShareFacebook: '[data-action="share-facebook"]',
        buttonShareTwitter: '[data-action="share-twitter"]',
        buttonShareColidoTalk: '[data-action="share-colido-talk"]',
    },

    events: {
        'click @ui.buttonClose': 'onActionClose',
        //'click @ui.buttonCopyLink': 'onCopyLink',
        'click @ui.buttonShareLabel': 'shareLabel',
        'click @ui.buttonShareEmail': 'shareByEmail',
        'click @ui.buttonShareFacebook': 'shareByFacebook',
        'click @ui.buttonShareTwitter': 'shareByTwitter',
        'click @ui.buttonShareColidoTalk': 'shareByColidoTalk',
        'change @ui.linkAccessToggle': 'onLinkAccessChanged',
    },

    /**
     * Reference to the share model
     * @type {ShareModel}
     */
    shareModel: null,

    /**
     * Reference for clipboard
     * @type {object}
     */
    clipboard: null,

    initialize: function(options) {
        ColidoLayoutView.prototype.initialize.apply(this, arguments);

        this.shareModel = new ShareModel({
            token: this.model.get('shareToken'),
            catalog: this.model.collection.catalog,
        });

        // Fetch shared model
        if (this.model.isShared()) {
            this.shareModel.fetch({
                success: function() {
                    this.updateShareUrl();
                }.bind(this),
                error: function() {
                    this.updateShareUrl();
                }.bind(this),
            });
        }
    },

    onShow: function() {
        this.shareColumnsRegion.show(new ShareColumnsListView({
            setModel: this.model,
            shareModel: this.shareModel,
        }));

        // Init copy to clipboard
        this.clipboard = new Clipboard('.btn[data-action="copy-link"]');
        this.clipboard.on('success', function(event) {
            if (!this.isDestroyed) {
                $(event.trigger).html(App.t('collection::set:share:copy-link:success'));
                event.clearSelection();
            }
        }.bind(this));

        this.clipboard.on('error', function(event) {
            Messenger().error({
                message: App.t('collection::set:share:copy-link:error'),
            });
        });
    },

    onBeforeDestroy: function() {
        if (this.clipboard && this.clipboard.off) {
            this.clipboard.off();
        }

        this.clipboard = null;
    },

    /**
     * Closes the modal
     * @param {object} event The click event from the button
     */
    onActionClose: function(event) {
        this.trigger('close:modal');
    },

    /**
     * Copy link stub
     * @param {object} event The click events
     */
    onCopyLink: function(event) {
        window.prompt('Copy to clipboard: Ctrl+C, Enter', this.ui.inputShareLink.val());
    },

    /**
     * Is triggered, when the user changed the share status
     */
    onLinkAccessChanged: function() {
        var val = this.ui.linkAccessToggle.filter(':checked').val();

        if (val == 1) {
            this.ui.linkAccessToggle.parent().addClass('is-loading');
            this.createShareItem();
        } else {
            if (confirm(App.t('share::confirm:disable:set:share'))) {
                // Destroy share item
                this.destroyShareItem();

                // Remove token, unless item was destroyed
                this.model.save({shareToken: null}, {patch: true});
            } else {
                this.ui.linkAccessToggle.filter('[value="1"]').prop('checked', true).focus();
            }
        }
    },

    /**
     * Creates a new share item
     */
    createShareItem: function() {
        this.shareModel = new ShareModel({
            type: 'set',
            typeId: this.model.id,
            catalog: this.model.collection.catalog,
        });

        this.shareModel.save({}, {
            wait: true,
            success: function(model) {
                var shareToken = model.id;

                // Set share token for set
                this.model.save({shareToken: shareToken}, {patch: true});

                //refresh columns list with new share model
                this.shareColumnsRegion.show(new ShareColumnsListView({
                    setModel: this.model,
                    shareModel: this.shareModel,
                }));

                // Update ui
                this.ui.linkAccessToggle.parent().removeClass('is-loading');
                this.ui.container.toggleClass('link-access-active', true);

                this.updateShareUrl();
            }.bind(this),
            error: function(model) {
                this.ui.linkAccessToggle.parent().removeClass('is-loading');
                this.ui.container.toggleClass('link-access-active', false);
                this.ui.linkAccessToggle.filter('[value="0"]').prop('checked', true);
                Messenger().error(App.t('share::share-set:response:error'));
            }.bind(this),
        });
    },

    /**
     * Destroys the share item
     */
    destroyShareItem: function() {
        if (this.shareModel && this.shareModel instanceof ShareModel) {
            this.shareModel.destroy({
                wait: true,
                success: function (model) {
                    this.ui.linkAccessToggle.parent().removeClass('is-loading');
                    this.ui.container.toggleClass('link-access-active', false);
                    this.updateShareUrl('');
                }.bind(this),
                error: function (model) {
                    this.ui.linkAccessToggle.parent().removeClass('is-loading');
                    Messenger().error(App.t('share::share-set:response:error'));
                }.bind(this),
            });
        }
    },

    shareLabel: function() {
        var share = this.shareModel;

        App.radio('collection').execute('print:label', {
            url: share.get('url'),
            title: share.get('title'),
        });
    },

    shareByEmail: function() {
        var subject = App.t('share::by-mail:subject', {name: this.model.get('name')});
        var content = App.t('share::by-mail:body', {
            name: this.model.get('name'),
            url: this.getShareUrl(),
        });

        window.location.href = _.tpl('mailto:?subject={subject}&body={body}', {
            subject: encodeURIComponent(subject),
            body: encodeURIComponent(content),
        });
    },

    shareByFacebook: function() {
        var share = this.shareModel;
        var image = share.get('image');

        var url = _.tpl('//www.facebook.com/dialog/feed?app_id=1607430859510290&display=popup&caption={caption}&link={url}&picture={image}&name={title}&redirect_uri={redirectUri}', {
            url: encodeURIComponent(share.get('url')),
            caption: encodeURIComponent(share.getCaption(this.model.get('items').length)),
            image: encodeURIComponent(share.getImageUrl('1200x1200')),
            title: encodeURIComponent(share.get('title')),
            redirectUri: 'https://shared.colido.de/close-window',
        });

        window.open(
            url,
            'facebook-share-dialog',
            'width=630,height=340'
        );
    },

    shareByTwitter: function() {
        var share = this.shareModel;

        var url = _.tpl('//twitter.com/share?url={url}&text={text}&via={via}',  {
            text: encodeURIComponent(App.t('share::by-twitter:body', {title: _.truncate(share.get('title'), 60, '…')})), //Check if restuling message is still under 140 chars if you change this
            url: encodeURIComponent(share.get('url')),
            via: 'colidoapp',
        });

        window.open(
            url,
            'twitter-share-dialog',
            'width=630,height=300'
        );
    },

    shareByColidoTalk: function() {
        var share = this.shareModel;

        var shareWithConversationModalView = new ShareWithConversationsModalView({shareUrl: share.get('url')});
        var options = {
            additionalClassName: 'modal-center modal-restriction modal-share-with-conversation',
        };
        App.addModal(shareWithConversationModalView, options);
    },

    /**
     * Updates the share URL in the ui
     */
    updateShareUrl: function(url) {
        url = url || this.getShareUrl();

        this.ui.inputShareLink.val(url);
        this.ui.buttonOpenLink.attr('href', url);
    },

    /**
     * Returns the share URL
     * @returns {string} The URL for sharing
     */
    getShareUrl: function() {
        return this.shareModel ? this.shareModel.get('url') : '';
    },
});

module.exports = ShareSetOverlayLayout;